import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Dumbbell, Users, Trophy, Calendar, Star, Quote } from 'lucide-react';
import { Button } from '../components/ui/button';

const Home = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://customer-assets.emergentagent.com/job_em-fitness/artifacts/obozf34y_IMG_7107.jpeg"
            alt="EM Coaching"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/50"></div>
        </div>
        
        <div className="relative z-10 container mx-auto px-6 text-white">
          <div className="max-w-3xl">
            <h1 className="text-6xl md:text-7xl font-bold mb-6 leading-tight animate-slide-up">
              Transformez Votre Corps,
              <span className="text-red-600"> Dépassez Vos Limites</span>
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-gray-300 animate-slide-up animation-delay-200">
              Coaching sportif professionnel à Berchem-Sainte-Agathe. 
              Plus de 17 ans d'expérience au service de votre transformation.
            </p>
            <div className="flex flex-wrap gap-4 animate-slide-up animation-delay-400">
              <Link to="/contact">
                <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white px-8 py-6 text-lg group">
                  Réserver Maintenant
                  <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              <Link to="/about">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-black px-8 py-6 text-lg">
                  Découvrir Coach Massa
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Stats */}
      <section className="py-16 bg-zinc-950">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {[
              { number: '17+', label: "Années d'Expérience" },
              { number: '6+', label: 'Ans de Coaching Pro' },
              { number: '1300+', label: 'Clients Satisfaits' },
              { number: '100%', label: 'Engagement' }
            ].map((stat, index) => (
              <div
                key={index}
                className="bg-zinc-900 p-6 rounded-xl text-center hover:bg-zinc-800 transition-all hover:scale-105 border border-zinc-800 hover:border-red-600"
              >
                <div className="text-4xl md:text-5xl font-bold text-red-600 mb-2">{stat.number}</div>
                <div className="text-gray-300 text-sm font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-20 bg-zinc-950">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Nos <span className="text-red-600">Services</span>
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Un accompagnement personnalisé adapté à vos objectifs
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: Dumbbell,
                title: 'Coaching Privé',
                description: 'Séance individuelle 100% personnalisée pour atteindre vos objectifs',
                image: 'https://customer-assets.emergentagent.com/job_em-fitness/artifacts/yzz428ta_IMG_7115.png'
              },
              {
                icon: Users,
                title: 'Coaching Duo',
                description: 'Entraînez-vous à deux pour une motivation décuplée',
                image: 'https://customer-assets.emergentagent.com/job_em-fitness/artifacts/r3momt1h_IMG_7116.png'
              },
              {
                icon: Users,
                title: 'Coaching Groupe',
                description: 'Sessions dynamiques en petit groupe pour plus d\'énergie',
                image: 'https://customer-assets.emergentagent.com/job_em-fitness/artifacts/dicl0r9b_IMG_7133.jpeg'
              },
              {
                icon: Calendar,
                title: 'Coaching En Ligne',
                description: 'Programme personnalisé à suivre où vous voulez, quand vous voulez',
                image: 'https://customer-assets.emergentagent.com/job_em-fitness/artifacts/3c2x2xi9_IMG_7134.jpeg'
              },
              {
                icon: Trophy,
                title: 'Programme Nutrition',
                description: 'Plan nutritionnel adapté à vos besoins et objectifs',
                image: 'https://customer-assets.emergentagent.com/job_em-fitness/artifacts/8txku760_IMG_7135.jpeg'
              },
              {
                icon: Trophy,
                title: 'Football Academy',
                description: 'Formation football pour tous niveaux avec un ancien pro',
                image: 'https://customer-assets.emergentagent.com/job_em-fitness/artifacts/uduswzpm_IMG_7110.png'
              }
            ].map((service, index) => (
              <div
                key={index}
                className="group relative overflow-hidden rounded-lg bg-zinc-900 hover:bg-zinc-800 transition-all duration-300 hover:scale-105"
              >
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent"></div>
                </div>
                <div className="p-6">
                  <div className="flex items-center mb-3">
                    <service.icon className="w-8 h-8 text-red-600 mr-3" />
                    <h3 className="text-xl font-bold text-white">{service.title}</h3>
                  </div>
                  <p className="text-gray-400">{service.description}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/services">
              <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white">
                Voir Tous les Services
                <ArrowRight className="ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-16 md:py-24 bg-zinc-950 overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-red-600 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-red-600 rounded-full blur-3xl"></div>
        </div>

        
        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <div className="max-w-5xl mx-auto">
            <div className="bg-gradient-to-br from-zinc-900 to-black border border-zinc-800 rounded-2xl p-6 md:p-12 lg:p-16 shadow-2xl">
              <div className="text-center">
                <div className="inline-block mb-4 md:mb-6">
                  <span className="px-3 py-1.5 md:px-4 md:py-2 bg-red-600/20 border border-red-600/50 rounded-full text-red-500 text-xs md:text-sm font-semibold">
                    Rejoignez-nous
                  </span>
                </div>
                
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 md:mb-6 leading-tight">
                  Prêt à Commencer Votre
                  <span className="block mt-1 md:mt-2 bg-gradient-to-r from-red-500 to-red-600 bg-clip-text text-transparent">
                    Transformation ?
                  </span>
                </h2>
                
                <p className="text-base md:text-xl text-gray-400 mb-6 md:mb-10 max-w-2xl mx-auto leading-relaxed">
                  Rejoignez des centaines de personnes qui ont déjà transformé leur vie avec Coach Massa
                </p>
                
                <div className="flex flex-col gap-3 md:gap-4 justify-center items-stretch md:items-center">
                  <Link to="/contact" className="w-full md:w-auto">
                    <Button size="lg" className="w-full md:w-auto bg-red-600 hover:bg-red-700 text-white px-6 md:px-8 py-5 md:py-6 text-base md:text-lg group shadow-lg shadow-red-600/25">
                      Réserver Votre Première Séance
                      <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </Link>
                  <Link to="/about" className="w-full md:w-auto">
                    <Button size="lg" variant="outline" className="w-full md:w-auto border-zinc-700 text-white hover:bg-zinc-800 px-6 md:px-8 py-5 md:py-6 text-base md:text-lg">
                      En Savoir Plus
                    </Button>
                  </Link>
                </div>
                
                <div className="mt-8 md:mt-12 flex flex-col md:flex-row justify-center items-center gap-4 md:gap-8 text-xs md:text-sm text-gray-500">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    <span>17+ ans d'expérience</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    <span>1300+ clients satisfaits</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Home;
